// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 20-agent-plugins.md#marketplace-repo-bootstrap

import * as React from 'react';

import { MonoValue } from '@/components/greenlight/MonoValue';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { PluginMarketplaceStatus } from '@greenlight/shared';

export interface UnregisterMarketplaceDialogProps {
  /** The repository staged for unregister, or null when closed. */
  repository: PluginMarketplaceStatus | null;
  pending: boolean;
  error: string | null;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

/**
 * Confirm step for retiring a `plugin_marketplaces` row (docs/20
 * §marketplace-repo-bootstrap). Mirrors DeleteIntegrationDialog's shape; the
 * one Greenlight-specific fact this dialog must state is that the GitHub
 * repository is untouched — only its managed-row membership ends.
 */
export function UnregisterMarketplaceDialog({
  repository,
  pending,
  error,
  onConfirm,
  onCancel,
}: UnregisterMarketplaceDialogProps): React.JSX.Element {
  return (
    <Dialog open={repository !== null} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="min-w-0 overflow-hidden sm:max-w-md">
        <DialogHeader className="min-w-0">
          <DialogTitle>Unregister agent plugin repo</DialogTitle>
          <DialogDescription className="break-words">
            {repository ? (
              <>
                Stop managing <MonoValue>{repository.repo_full_name}</MonoValue> as a Greenlight
                agent plugin repo. The repository itself is left exactly as it is on GitHub —
                Greenlight never deletes or archives it. Agents pointed at it stop receiving
                template updates until it is bootstrapped again.
              </>
            ) : (
              ''
            )}
          </DialogDescription>
        </DialogHeader>

        {error && <p className="text-[11.5px] text-destructive">{error}</p>}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={pending}
            onClick={() => void onConfirm()}
          >
            {pending ? 'Unregistering…' : 'Unregister repo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
